import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FileText, Clock, CheckCircle2, Cpu } from 'lucide-react';
import { wiseboardService } from '@/services/wiseboardService';
import { useWiseboardRefresh } from '../context/WiseboardRefreshContext';
import type { GPUStats } from '../types/wiseboard';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('GPUStatsCards', 'wiseboard');

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
}

function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  gpuStats: GPUStats[];
  loading?: boolean;
}

function StatCard({ title, value, subtitle, icon, color, gpuStats, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <CardContent className="pt-4">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-32 mb-1" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Card className="cursor-pointer transition-shadow hover:shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                {icon}
                <span>{title}</span>
              </div>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <div className="text-xs text-muted-foreground">{subtitle}</div>
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent className="w-80 p-4" side="bottom">
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-semibold">
              <Cpu className="h-4 w-4" />
              GPU Performance Breakdown
            </div>
            {gpuStats.filter(g => g.gpu_id !== 'unknown').map((gpu) => (
              <div key={gpu.gpu_id} className="border-t pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{gpu.gpu_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {gpu.gpu_id === 'gpu2' ? 'Primary' : 'Overflow'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Jobs:</span>{' '}
                    <span className="font-medium text-green-600">{gpu.jobs_completed}</span>
                    {gpu.jobs_failed > 0 && (
                      <span className="text-red-600 ml-1">({gpu.jobs_failed} failed)</span>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pages:</span>{' '}
                    <span className="font-medium">{formatNumber(gpu.total_pages)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Chars:</span>{' '}
                    <span className="font-medium text-purple-600">{formatNumber(gpu.total_chars)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Avg/page:</span>{' '}
                    <span className="font-medium text-amber-600">{formatDuration(gpu.avg_per_page_ms)}</span>
                  </div>
                </div>
              </div>
            ))}
            {gpuStats.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-2">
                No GPU data available
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function GPUStatsCards() {
  const [gpuStats, setGpuStats] = useState<GPUStats[]>([]);
  const [loading, setLoading] = useState(true);
  const pendingUpdates = useRef<unknown[]>([]);
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchStats = useCallback(async () => {
    const result = await wiseboardService.getGPUProcessingStats();
    if (result.error) {
      log.error('Failed to fetch GPU stats', { error: result.error.message });
    } else {
      setGpuStats(result.data);
      log.info('GPU stats updated', { gpuCount: result.data.length });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchStats();

    // Subscribe to real-time updates
    const channel = wiseboardService.subscribeToDocumentQueue(() => {
      pendingUpdates.current.push({});

      // Debounce updates - wait 200ms for more updates before refreshing
      if (!batchTimeout.current) {
        batchTimeout.current = setTimeout(() => {
          log.info('Processing batched queue updates', { count: pendingUpdates.current.length });
          fetchStats();
          pendingUpdates.current = [];
          batchTimeout.current = null;
        }, 200);
      }
    });

    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      channel.unsubscribe();
    };
  }, [fetchStats]);

  const { registerRefresh } = useWiseboardRefresh();
  useEffect(() => registerRefresh(fetchStats), [registerRefresh, fetchStats]);

  // Calculate totals from GPU stats
  const totals = gpuStats.reduce(
    (acc, gpu) => ({
      totalChars: acc.totalChars + gpu.total_chars,
      totalPages: acc.totalPages + gpu.total_pages,
      jobsCompleted: acc.jobsCompleted + gpu.jobs_completed,
      jobsFailed: acc.jobsFailed + gpu.jobs_failed,
      totalExtractionMs: acc.totalExtractionMs + (gpu.avg_extraction_ms * gpu.jobs_completed)
    }),
    { totalChars: 0, totalPages: 0, jobsCompleted: 0, jobsFailed: 0, totalExtractionMs: 0 }
  );

  const avgPerPageMs = totals.totalPages > 0
    ? totals.totalExtractionMs / totals.totalPages
    : 0;

  return (
    <div className="grid grid-cols-3 gap-4 mb-4">
      <StatCard
        title="Characters Extracted"
        value={formatNumber(totals.totalChars)}
        subtitle={`${formatNumber(totals.totalPages)} pages processed`}
        icon={<FileText className="h-4 w-4" />}
        color="text-purple-600"
        gpuStats={gpuStats}
        loading={loading}
      />
      <StatCard
        title="Avg Per-Page Time"
        value={formatDuration(avgPerPageMs)}
        subtitle="extraction time per page"
        icon={<Clock className="h-4 w-4" />}
        color="text-amber-600"
        gpuStats={gpuStats}
        loading={loading}
      />
      <StatCard
        title="Jobs Completed"
        value={`${totals.jobsCompleted}`}
        subtitle={totals.jobsFailed > 0 ? `${totals.jobsFailed} failed` : 'all successful'}
        icon={<CheckCircle2 className="h-4 w-4" />}
        color="text-green-600"
        gpuStats={gpuStats}
        loading={loading}
      />
    </div>
  );
}

export default GPUStatsCards;
